"use client";

import { useEffect, useMemo, useState } from "react";
import { useCurrentHint } from "@/contexts/hint-context";
import { buildLayout } from "@/lib/virtuosa-layout/layout";
import { placeChord } from "@/lib/virtuosa-layout/place-chord";
import { placeVoicing } from "@/lib/virtuosa-layout/place-voicing";
import {
  AZERTY_LABELS,
  resolveLayoutLabels,
} from "@/lib/virtuosa-layout/labels";
import type { LayoutCode, LayoutKey } from "@/lib/virtuosa-layout/types";

interface PillPlacement {
  left: LayoutKey[];
  right: LayoutKey[];
  hasBass: boolean;
  /** Notes of the hint this keyboard cannot reach at all. */
  unreachable: number;
}

/**
 * Compact pill displayed next to the FAB whenever a producer has published
 * a hint. Resolves the keyboard keys via `placeVoicing` (explicit MIDIs,
 * e.g. an inversion being practiced) or `placeChord` (abstract
 * root-position fallback).
 *
 * `midis` is read as a voicing when neither field is set. Every producer
 * publishes it — it is what the studio piano lights — and the two say the
 * same thing: `voicing` is documented as "the exact MIDI notes the user is
 * meant to play right now", which is what `midis` holds. Without this the
 * pill answered `null` for every producer that did not *also* restate its
 * notes under a second key, and the learning journey — the biggest of them
 * — was silently invisible here while lighting the studio piano.
 */
export function HintFabPill() {
  const hint = useCurrentHint();
  const layout = useMemo(() => buildLayout(), []);
  const [labels, setLabels] = useState<Record<LayoutCode, string>>(
    () => AZERTY_LABELS,
  );

  useEffect(() => {
    let cancelled = false;
    const codes = Array.from(layout.byCode.keys());

    resolveLayoutLabels(codes).then((resolved) => {
      if (!cancelled) {
        setLabels(resolved);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [layout]);

  const chord = hint?.chord ?? null;
  const voicing = useMemo<number[] | null>(() => {
    if (hint?.voicing && hint.voicing.length > 0) {
      return hint.voicing;
    }

    if (!hint || hint.midis.length === 0) {
      return null;
    }

    return [...new Set(hint.midis.map((n) => n.midi))].sort((a, b) => a - b);
  }, [hint]);

  const placement = useMemo<PillPlacement | null>(() => {
    if (voicing && voicing.length > 0) {
      // Filtered to what this keyboard has a cell for, *before* placing.
      // The lattice covers F#2 to F#5, so a bass an octave below it has no
      // key — and `pickMostCompact` answers an empty list as soon as one
      // note has no candidate, which blanked the pill entirely. Showing the
      // reachable keys and counting the rest beats showing nothing at all
      // and never saying why.
      const reachable = voicing.filter(
        (midi) => (layout.byMidi.get(midi)?.length ?? 0) > 0,
      );
      const unreachable = voicing.length - reachable.length;

      if (reachable.length === 0) {
        return null;
      }

      const v = placeVoicing(reachable, layout);

      if (!v) {
        return null;
      }

      return {
        left: v.left,
        right: v.right,
        hasBass: v.left.length > 0,
        unreachable,
      };
    }

    if (chord) {
      const c = placeChord({ ...chord, withBass: true }, layout);

      if (!c) {
        return null;
      }

      return {
        left: c.left,
        right: c.right,
        hasBass: c.hasBass,
        unreachable: 0,
      };
    }

    return null;
  }, [voicing, chord, layout]);

  if (!placement || (placement.left.length === 0 && placement.right.length === 0)) {
    return null;
  }

  const bassLabel = placement.left
    .map((k) => labels[k.code] ?? k.code)
    .join("");
  const triadLabel = placement.right
    .map((k) => labels[k.code] ?? k.code)
    .join(" ");

  return (
    <div
      className="pointer-events-none flex h-8 items-center gap-2 rounded-full border border-amber-300/40 bg-bg-elevated/85 px-3 font-mono text-[11px] font-medium text-amber-200 shadow-md backdrop-blur-md"
      aria-label={
        placement.unreachable > 0
          ? `Touches à jouer : ${triadLabel}. ${placement.unreachable} note hors du clavier.`
          : `Touches à jouer : ${triadLabel}.`
      }
    >
      {placement.hasBass ? (
        <>
          <span className="text-amber-300/80">B</span>
          <span>{bassLabel}</span>
          <span className="text-amber-300/40">·</span>
        </>
      ) : null}
      <span>{triadLabel}</span>
      {placement.unreachable > 0 ? (
        <span className="text-amber-300/50" title="hors du clavier">
          +{placement.unreachable}
        </span>
      ) : null}
    </div>
  );
}
